import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../../app/LanguageContext';
import MediaGallery, { PRODUCT_DEFAULT_IMAGE } from '../../../modules/medias/public/MediaGallery';
import { mediaFileUrl } from '../private/catalogCompositionUtils';

const emptyItem = () => ({
  title: '',
  media_id: '',
  image_url: '',
});

const normalizeItem = (item) => ({
  title: item?.title ?? '',
  media_id: item?.media_id ?? '',
  image_url: item?.image_url || (item?.file_path ? mediaFileUrl(item.file_path) : ''),
});

const ItemListEditor = ({ items, readOnly, onChange, minItems = 0, maxItems = 0 }) => {
  const { translate } = useTranslation();
  const list = Array.isArray(items) ? items.map(normalizeItem) : [];
  const min = Math.max(0, Number(minItems) || 0);
  const max = Number(maxItems) > 0 ? Number(maxItems) : 0;
  const canAdd = !readOnly && (max <= 0 || list.length < max);
  const canRemove = (index) => !readOnly && list.length > min;

  const emitItems = (nextItems) => {
    let next = nextItems.map(normalizeItem);
    if (max > 0 && next.length > max) {
      next = next.slice(0, max);
    }
    onChange?.(next);
  };

  const updateItem = (index, patch) => {
    emitItems(list.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const addItem = () => {
    if (!canAdd) return;
    emitItems([...list, emptyItem()]);
  };

  const removeItem = (index) => {
    if (!canRemove(index)) return;
    emitItems(list.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {(min > 0 || max > 0) && (
        <Typography variant="caption" color="text.secondary">
          {max > 0
            ? translate('catalog.templates.list_items_limits', { min, max })
            : translate('catalog.templates.list_items_limits_min_only', { min })}
        </Typography>
      )}
      {list.map((item, index) => (
        <Paper key={`item-${index}`} variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {translate('catalog.templates.item_list_item', { index: index + 1 })}
            </Typography>
            {!readOnly && (
              <IconButton size="small" onClick={() => removeItem(index)} disabled={!canRemove(index)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <TextField
            label={translate('catalog.templates.item_list_title')}
            size="small"
            fullWidth
            disabled={readOnly}
            value={item.title}
            onChange={(e) => updateItem(index, { title: e.target.value })}
            sx={{ mb: 1.5 }}
          />
          <MediaGallery
            value={
              item.media_id
                ? [{ id: item.media_id, url: item.image_url || PRODUCT_DEFAULT_IMAGE }]
                : item.image_url
                  ? [{ id: `url-${index}`, url: item.image_url }]
                  : []
            }
            maxItems={1}
            readOnly={readOnly}
            onChange={(medias) => {
              const first = medias[0];
              updateItem(index, {
                media_id: first?.id?.startsWith('url-') ? '' : first?.id || '',
                image_url: first ? mediaFileUrl(first.file_path || first.url) : '',
              });
            }}
          />
        </Paper>
      ))}
      {!readOnly && (
        <Button size="small" startIcon={<AddIcon />} onClick={addItem} disabled={!canAdd}>
          {translate('catalog.templates.item_list_add')}
        </Button>
      )}
    </Box>
  );
};

export default ItemListEditor;
